import React from 'react';
import { usePermissions } from '@/hooks/usePermissions';

interface PermissionGuardProps {
  module: string;
  action: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAll?: boolean;
  permissions?: Array<{ module: string; action: string }>;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  module,
  action,
  children,
  fallback = null,
  requireAll = false,
  permissions,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();

  if (isLoading) {
    return fallback;
  }

  let hasAccess = false;

  if (permissions && permissions.length > 0) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  } else {
    hasAccess = hasPermission(module, action);
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

interface ConditionalRenderProps {
  module: string;
  action: string;
  children: (hasPermission: boolean) => React.ReactNode;
}

export const ConditionalRender: React.FC<ConditionalRenderProps> = ({
  module,
  action,
  children,
}) => {
  const { hasPermission, isLoading } = usePermissions();

  if (isLoading) {
    return null;
  }

  return <>{children(hasPermission(module, action))}</>;
};

// Higher-order component for permission-based rendering
export const withPermission = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  module: string,
  action: string,
  fallback?: React.ReactNode
) => {
  const WithPermissionComponent = (props: P) => (
    <PermissionGuard module={module} action={action} fallback={fallback}>
      <WrappedComponent {...props} />
    </PermissionGuard>
  );

  WithPermissionComponent.displayName = `withPermission(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithPermissionComponent;
};
